import { BulkStockErrorFragment } from "@saleor/fragments/types/BulkStockErrorFragment";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { StockErrorFragment } from "@saleor/fragments/types/StockErrorFragment";
import { decimal, stringToFloat } from "@saleor/misc";
import { ProductUpdatePageSubmitData } from "@saleor/products/components/ProductUpdatePage";
import { ProductDetails_product } from "@saleor/products/types/ProductDetails";
import { ProductImageCreateVariables } from "@saleor/products/types/ProductImageCreate";
import { ProductImageReorderVariables } from "@saleor/products/types/ProductImageReorder";
import {
  ProductSetAvailabilityForPurchase,
  ProductSetAvailabilityForPurchaseVariables
} from "@saleor/products/types/ProductSetAvailabilityForPurchase";
import {
  ProductUpdate,
  ProductUpdateVariables
} from "@saleor/products/types/ProductUpdate";
import { ProductVariantCreateData_product } from "@saleor/products/types/ProductVariantCreateData";
import { ProductVariantDetails_productVariant_product } from "@saleor/products/types/ProductVariantDetails";
import { ProductVariantReorderVariables } from "@saleor/products/types/ProductVariantReorder";
import {
  SimpleProductUpdate,
  SimpleProductUpdateVariables
} from "@saleor/products/types/SimpleProductUpdate";
import {
  SimpleProductVariantUpdate,
  SimpleProductVariantUpdateVariables
} from "@saleor/products/types/SimpleProductVariantUpdate";
import { mapFormsetStockToStockInput } from "@saleor/products/utils/data";
import { getProductAvailabilityVariables } from "@saleor/products/utils/handlers";
import { ReorderEvent } from "@saleor/types";
import { convertFromRaw } from "draft-js";
import { MutationFetchResult } from "react-apollo";
import { arrayMove } from "react-sortable-hoc";

import { ProductStatusEnum } from "../../../types/globalTypes";

export function createUpdateHandler(
  isStoreAdmin: boolean,
  product: ProductDetails_product,
  updateProduct: (
    variables: ProductUpdateVariables
  ) => Promise<MutationFetchResult<ProductUpdate>>,
  updateSimpleProduct: (
    variables: SimpleProductUpdateVariables
  ) => Promise<MutationFetchResult<SimpleProductUpdate>>,
  setProductAvailability: (
    variables: ProductSetAvailabilityForPurchaseVariables
  ) => Promise<MutationFetchResult<ProductSetAvailabilityForPurchase>>,
  simpleProductVariantUpdate: (
    variables: SimpleProductVariantUpdateVariables
  ) => Promise<MutationFetchResult<SimpleProductVariantUpdate>>
) {
  const checkSKU = sku => {
    if (product && product.variants.length === 0) {
      return { sku };
    }
    if (product && product.variants[0].sku !== sku) {
      return { sku };
    }
    return {};
  };
  return async (data: ProductUpdatePageSubmitData) => {
    let productStatusValue = null;
    if (data.productStatus === "DRAFT") {
      productStatusValue = ProductStatusEnum.DRAFT;
    } else if (data.productStatus === "READY") {
      productStatusValue = ProductStatusEnum.READY;
    } else if (data.productStatus === "UNLISTED") {
      productStatusValue = ProductStatusEnum.UNLISTED;
    }

    // console.log(data.description);
    // console.log(convertFromRaw(data.description).getPlainText());

    const productVariables: ProductUpdateVariables = {
      id: product.id,
      input: {
        attributes: data.attributes.map(attribute => ({
          id: attribute.id,
          values: attribute.value[0] === "" ? [] : attribute.value
        })),
        basePrice: decimal(data.basePrice),
        brand: data.brand,
        brandLabel: data.brandLabel,
        category: data.category,
        chargeTaxes: data.chargeTaxes,
        collections: data.collections,
        description: convertFromRaw(data.description).getPlainText(),
        descriptionJson: JSON.stringify(data.description),
        height: data.height === "" ? null : data.height,
        isPublished: data.isPublished,
        isSimpleProduct: data.isSimpleProduct,
        length: data.length === "" ? null : data.length,
        name: data.name,
        nonCod: data.nonCod,
        productStatus: productStatusValue,
        publicationDate:
          data.publicationDate !== "" ? data.publicationDate : null,
        seo: {
          description: data.seoDescription,
          title: data.seoTitle
        },
        slug: data.slug,
        tags: data.tags.map(tag => tag.label),
        taxCode: data.changeTaxCode ? data.taxCode : null,
        visibleInListings: data.visibleInListings,
        weight: data.weight === "" ? null : data.weight,
        width: data.width === "" ? null : data.width
      }
    };

    let errors: Array<
      ProductErrorFragment | StockErrorFragment | BulkStockErrorFragment
    >;

    if (!data.isSimpleProduct) {
      const result = await updateProduct(productVariables);
      errors = result.data.productUpdate.errors;
    } else {
      const result = await updateSimpleProduct({
        ...productVariables,
        // addStocks: data.addStocks.map(mapFormsetStockToStockInput),
        // deleteStocks: data.removeStocks,
        input: {
          ...productVariables.input,
          weight: stringToFloat(data.weight),
          ...checkSKU(data.sku),
          trackInventory: data.trackInventory
        }
        // productVariantId: product.variants[0].id,
        // productVariantInput: {
        //   sku: data.sku,
        //   trackInventory: data.trackInventory
        // },
        // updateStocks: data.updateStocks.map(mapFormsetStockToStockInput)
      });
      errors = [
        ...result.data.productUpdate.errors
        // ...result.data.productVariantStocksCreate.errors,
        // ...result.data.productVariantStocksDelete.errors,
        // ...result.data.productVariantStocksUpdate.errors,
        // ...result.data.productVariantUpdate.errors
      ];
    }

    const { isAvailableForPurchase, availableForPurchase } = data;
    // Form seeds Available from product.isAvailable (see getVisibilityFormValues).
    if (
      isAvailableForPurchase !== !!product.isAvailable ||
      availableForPurchase !== product.availableForPurchase
    ) {
      const variables = getProductAvailabilityVariables({
        availableForPurchase,
        isAvailableForPurchase,
        productId: product.id
      });
      const availabilityResult = await setProductAvailability(variables);

      errors = [
        ...errors,
        ...availabilityResult.data.productSetAvailabilityForPurchase.errors
      ];
    }

    if (isStoreAdmin) {
      const result = await simpleProductVariantUpdate({
        addStocks: data.addStocks.map(mapFormsetStockToStockInput),
        deleteStocks: data.removeStocks,
        productVariantId: product.variants[0].id,
        updateStocks: data.updateStocks.map(mapFormsetStockToStockInput)
        // productVariantInput: {
        //   sku: data.sku,
        //   trackInventory: data.trackInventory,
        //   stockOnMall: data.stockOnMall
        // },
      });
      errors = [
        ...result.data.productVariantStocksCreate.errors,
        ...result.data.productVariantStocksDelete.errors,
        ...result.data.productVariantStocksUpdate.errors
        // ...result.data.productVariantUpdate.errors
      ];
    }

    return errors;
  };
}

export function createImageUploadHandler(
  id: string,
  createProductImage: (variables: ProductImageCreateVariables) => void
) {
  return (file: File) =>
    createProductImage({
      alt: "",
      image: file,
      product: id
    });
}

export function createImageReorderHandler(
  product: ProductDetails_product,
  reorderProductImages: (variables: ProductImageReorderVariables) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    let ids = product.images.map(image => image.id);
    ids = arrayMove(ids, oldIndex, newIndex);
    reorderProductImages({
      imagesIds: ids,
      productId: product.id
    });
  };
}

export function createVariantReorderHandler(
  product:
    | ProductDetails_product
    | ProductVariantDetails_productVariant_product
    | ProductVariantCreateData_product,
  reorderProductVariants: (variables: ProductVariantReorderVariables) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    reorderProductVariants({
      move: {
        id: product.variants[oldIndex].id,
        sortOrder: newIndex - oldIndex
      },
      productId: product.id
    });
  };
}
